import React, { useState, useRef, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styles from "../Styles/Pages/Profile.module.css";
import {
	FaUser,
	FaHeart,
	FaSignOutAlt,
	FaEdit,
	FaLock, 
	FaTimes, 
	FaSpinner, // Loading spinner
} from "react-icons/fa";
import { toast } from "react-toastify";
import Navbar from "../Components/UI/Navbar";
import AuthContext from "../store/AuthContext";
import { BASE_URL_API } from "../References/urls";
import JobCard from "../Pages/LandingPages/components/JobsListingCategorised/JobCard";
import { PageLoader } from "../Navigator/MainNavigator";

const Profile = () => {
	const authCtx = useContext(AuthContext);
	const [activeTab, setActiveTab] = useState("profile");
	const fileInputRef = useRef(null);

	// Ref for auto-focusing input inside modal
	const editInputRef = useRef(null);

	// --- NEW: Ref for scrolling content to top ---
	const contentRef = useRef(null);

	const [isLoadingUserData, setIsLoadingUserData] = useState(false);
	const [userRole, setUserRole] = useState("");
	const [secondaryWorkDomain, setSecondaryWorkDomain] = useState("");
	const [tertiaryWorkDomain, setTertiaryWorkDomain] = useState("");

	// Saved Jobs State
	const [savedJobs, setSavedJobs] = useState([]);
	const [isLoadingSavedJobs, setIsLoadingSavedJobs] = useState(false);
	const [savedJobsPagination, setSavedJobsPagination] = useState({});
	const [currentPage, setCurrentPage] = useState(1);
	const [hasMore, setHasMore] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const lastJobRef = useRef(null);
	const navigate = useNavigate();
	const location = useLocation();

	// Profile Form Data
	const [formData, setFormData] = useState({
		name: "",
		photo: "",
		currentAddress: "",
		gender: "",
		maritalStatus: "",
		phone: "",
		whatsapp: "",
		about: "",
		dob: "",
	});

	// State for the new Edit Modal
	const [editModal, setEditModal] = useState({
		isOpen: false,
		fieldKey: null, // 'maritalStatus', 'whatsapp', etc.
		label: "",
		type: "text", // 'text', 'textarea', 'select'
	});
	const [editValue, setEditValue] = useState("");
	const [isSavingField, setIsSavingField] = useState(false);
	const [user, setUser] = useState(null);

	// --- Handlers ---

	const handlePhotoClick = () => {
		fileInputRef.current.click();
	};

	const handleFileChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			const imageUrl = URL.createObjectURL(file);
			setFormData({ ...formData, photo: imageUrl });
			toast.success("Profile photo updated!");
			// Note: You might want to implement an actual API upload here
		}
	};

	const handleLogout = () => {
		localStorage.setItem("logoutJustHappened", "1");
		authCtx.logout();
		toast.success("Logged out successfully!");
		navigate("/", { replace: true });
	};

	// --- Data Fetching ---

	const getUserProfileData = async () => {
		setIsLoadingUserData(true);
		try {
			const response = await fetch(BASE_URL_API + "/getUserProfileData", {
				method: "POST",
				body: JSON.stringify({
					token: authCtx.token,
					userIdentifier: authCtx.userIdentifier,
				}),
			});

			if (!response.ok) {
				toast.error("Failed to load profile data.");
			} else {
				const respo = await response.json();
				if (respo.status === "success") {
					const userData = respo.response;
					setUser(userData);
					const newFormData = {
						name: userData.fullName || "",
						photo: userData.profilePic || "",
						currentAddress: userData.stylistAddress || "",
						gender: userData.gender === "MALE" ? "Male" : userData.gender === "FEMALE" ? "Female" : "Other",
						maritalStatus: userData.maritalStatus || "",
						phone: userData.phoneNumber || "",
						whatsapp: userData.whatsappNumber || "",
						about: userData.stylistDescription || "",
						dob: userData.dateOfBirth ? userData.dateOfBirth.split(" ")[0] : "",
					};
					setFormData(newFormData);
					setUserRole(userData.primaryWorkDomain || "");
					setSecondaryWorkDomain(userData.secondaryWorkDomain || "");
					setTertiaryWorkDomain(userData.tertiaryWorkDomain || "");
					authCtx.setProfilePic(userData.profilePic);
				} else {
					if (
						respo.message === "token Expired, please login again!"
					) {
						authCtx.logout();
						toast.error("Session expired. Please login again.");
					} else {
						toast.error(
							respo.message || "Failed to load profile data."
						);
					}
				}
			}
		} catch (error) {
			console.error(error);
			toast.error("Network error");
		}
		setIsLoadingUserData(false);
	};

	const updateProfileContent = async (updationKey, updationValue) => {
		try {
			const response = await fetch(
				BASE_URL_API + "/updateUserProfileContent",
				{
					method: "POST",
					body: JSON.stringify({
						token: authCtx.token,
						updationKey,
						updationValue,
						userIdentifier: authCtx.userIdentifier,
					}),
				}
			);
			if (!response.ok) throw new Error("Network error");
			const respo = await response.json();
			return {
				success: respo.status === "success",
				message: respo.message,
			};
		} catch (error) {
			return { success: false, message: "Network error" };
		}
	};

	// --- New Modal Edit Logic ---

	const openEditModal = (fieldKey, label, type = "text") => {
		setEditModal({
			isOpen: true,
			fieldKey,
			label,
			type,
		});
		setEditValue(formData[fieldKey] || "");
	};

	const closeEditModal = () => {
		setEditModal({ ...editModal, isOpen: false });
		setEditValue("");
	};

	const saveEditModal = async (e) => {
		e.preventDefault(); // Prevent form submit
		const field = editModal.fieldKey;
		setIsSavingField(true);

		// Map frontend keys to backend API keys
		const apiKey = {
			maritalStatus: "maritalStatus",
			about: "stylistDescription",
			currentAddress: "stylistAddress",
			whatsapp: "whatsappNumber",
		}[field];

		const result = await updateProfileContent(apiKey, editValue);

		if (result.success) {
			toast.success(`${editModal.label} updated successfully`);
			setFormData((prev) => ({ ...prev, [field]: editValue }));
			closeEditModal();
		} else {
			toast.error(`${editModal.label} update failed: ${result.message}`);
		}
		setIsSavingField(false);
	};

	// Auto-focus input when modal opens
	useEffect(() => {
		if (editModal.isOpen && editInputRef.current) {
			// Small timeout to ensure rendering is complete
			setTimeout(() => {
				editInputRef.current.focus();
			}, 50);
		}
	}, [editModal.isOpen]);

	// --- Saved Jobs Logic ---

	const getSavedJobs = async (page = 1) => {
		const isInitialLoad = page === 1;
		if (isInitialLoad) {
			setIsLoadingSavedJobs(true);
			setSavedJobs([]);
			setCurrentPage(1);
		} else {
			setLoadingMore(true);
		}
		try {
			const response = await fetch(BASE_URL_API + "/getSavedJobs", {
				method: "POST",
				body: JSON.stringify({
					token: authCtx.token,
					userIdentifier: authCtx.userIdentifier,
					page: page,
				}),
			});

			if (!response.ok) {
				if (isInitialLoad) setSavedJobs([]);
			} else {
				const respo = await response.json();
				if (respo.status === "success") {
					const newJobs = respo.response.jobs || [];
					if (isInitialLoad) {
						setSavedJobs(newJobs);
					} else {
						setSavedJobs((prev) => [...prev, ...newJobs]);
					}
					setSavedJobsPagination(respo.response.pagination);
					const pagination = respo.response.pagination;
					setHasMore(pagination.has_more);
					setCurrentPage(pagination.current_page);
				} else {
					if (isInitialLoad) {
						setSavedJobs([]);
					}
				}
			}
		} catch (error) {
			if (isInitialLoad) {
				setSavedJobs([]);
				toast.error("Network error");
			}
		}
		if (isInitialLoad) {
			setIsLoadingSavedJobs(false);
		} else {
			setLoadingMore(false);
		}
	};

	const loadMoreSavedJobs = () => {
		const nextPage = currentPage + 1;
		getSavedJobs(nextPage);
	};

	const isReadOnly = (fieldName) => {
		const lockedFields = ["name", "dob", "gender", "phone"];
		return lockedFields.includes(fieldName);
	};

	const renderProfileAvatar = () => {
		if (formData.photo) {
			return (
				<img
					src={formData.photo}
					alt="Profile"
					className={styles.avatar}
				/>
			);
		} else if (formData.name && formData.name.trim().length > 0) {
			return (
				<div className={`${styles.avatar} ${styles.avatarPlaceholder}`}>
					{formData.name.charAt(0).toUpperCase()}
				</div>
			);
		} else {
			return (
				<div className={`${styles.avatar} ${styles.avatarPlaceholder}`}>
					<FaUser />
				</div>
			);
		}
	};

	// --- Main Content Renderer ---

	const renderContent = () => {
		switch (activeTab) {
			case "saved":
				if (isLoadingSavedJobs) {
					return <PageLoader />;
				}
				if (savedJobs.length === 0) {
					return (
						<div className={styles.placeholderTab}>
							<h3>Saved Jobs</h3>
							<p>No saved jobs found.</p>
						</div>
					);
				}
				return (
					<div className={styles.savedJobsContainer}>
						<h3 className={styles.pageSubtitle}>
							Saved Jobs ({savedJobsPagination.total_records})
						</h3>
						<div className={styles.jobsGrid}>
							{savedJobs.map((job, index) => (
								<div
									key={job.jobIdentifier}
									ref={
										index === savedJobs.length - 1
											? lastJobRef
											: null
									}
								>
									<JobCard
										job={job}
										isBookmarked={true}
										onToggleBookmark={() => {}}
										onRemove={(jobIdentifier) => {
											setSavedJobs((prev) =>
												prev.filter(
													(j) =>
														j.jobIdentifier !==
														jobIdentifier
												)
											);
											setSavedJobsPagination((prev) => ({
												...prev,
												total_records: prev.total_records - 1,
											}));
										}}
									/>
								</div>
							))}
						</div>
						{loadingMore && (
							<div className={styles.loadingContainer}>
								<p>Loading more jobs...</p>
							</div>
						)}
					</div>
				);
			case "profile":
			default:
				if (isLoadingUserData) {
					return (
						<div className={styles.loadingContainer}>
							<p>Loading profile data...</p>
						</div>
					);
				}
				return (
					<form
						className={styles.formGrid}
						onSubmit={(e) => e.preventDefault()}
					>
						{/* ROW 1: Name & DOB */}
						<div className={styles.inputGroup}>
							<label>
								Full Name{" "}
								{isReadOnly("name") && (
									<FaLock
										className={styles.lockIcon}
										title="Locked"
									/>
								)}
							</label>
							<input
								type="text"
								value={formData.name}
								readOnly={true}
								className={styles.readOnly}
								placeholder="Enter your name!"
							/>
						</div>

						<div className={styles.inputGroup}>
							<label>
								Date of Birth{" "}
								{isReadOnly("dob") && (
									<FaLock
										className={styles.lockIcon}
										title="Locked"
									/>
								)}
							</label>
							<input
								type="date"
								value={formData.dob}
								readOnly={true}
								className={styles.readOnly}
							/>
						</div>

						{/* ROW 2: Gender & Phone */}
						<div className={styles.inputGroup}>
							<label>
								Gender{" "}
								<FaLock
									className={styles.lockIcon}
									title="Locked"
								/>
							</label>
							<select
								value={formData.gender}
								disabled={true}
								className={styles.readOnly}
							>
								<option value="">Select Gender</option>
								<option value="Male">Male</option>
								<option value="Female">Female</option>
								<option value="Other">Other</option>
							</select>
						</div>

						<div className={styles.inputGroup}>
							<label>
								Phone Number{" "}
								<span className={styles.required}>*</span>{" "}
								<FaLock
									className={styles.lockIcon}
									title="Locked"
								/>
							</label>
							<input
								type="tel"
								value={formData.phone}
								readOnly={true}
								className={styles.readOnly}
								placeholder="Enter your phone number"
							/>
						</div>

						{/* ROW 3: Marital Status & WhatsApp */}
						<div className={styles.inputGroup}>
							<div className={styles.labelRow}>
								<label>Marital Status</label>
								<FaEdit
									className={styles.editIcon}
									onClick={() =>
										openEditModal(
											"maritalStatus",
											"Marital Status",
											"select"
										)
									}
								/>
							</div>
							<input
								type="text"
								value={formData.maritalStatus}
								readOnly={true}
								className={styles.displayInput}
								placeholder="Enter your marital status"
								onClick={() =>
									openEditModal(
										"maritalStatus",
										"Marital Status",
										"select"
									)
								}
							/>
						</div>

						<div className={styles.inputGroup}>
							<div className={styles.labelRow}>
								<label>
									WhatsApp Number{" "}
									<span className={styles.required}>*</span>
								</label>
								<FaEdit
									className={styles.editIcon}
									onClick={() =>
										openEditModal(
											"whatsapp",
											"WhatsApp Number",
											"text"
										)
									}
								/>
							</div>
							<input
								type="tel"
								value={formData.whatsapp}
								readOnly={true}
								className={styles.displayInput}
								placeholder="Enter your whatsapp number"
								onClick={() =>
									openEditModal(
										"whatsapp",
										"WhatsApp Number",
										"text"
									)
								}
							/>
						</div>

						{/* ROW 4: Address & About */}
						<div className={styles.inputGroup}>
							<div className={styles.labelRow}>
								<label>Address</label>
								<FaEdit
									className={styles.editIcon}
									onClick={() =>
										openEditModal(
											"currentAddress",
											"Address",
											"textarea"
										)
									}
								/>
							</div>
							<textarea
								value={formData.currentAddress}
								readOnly={true}
								className={`${styles.bioArea} ${styles.displayInput}`}
								placeholder="Enter your address"
								onClick={() =>
									openEditModal(
										"currentAddress",
										"Address",
										"textarea"
									)
								}
							/>
						</div>

						<div className={styles.inputGroup}>
							<div className={styles.labelRow}>
								<label>About Me</label>
								<FaEdit
									className={styles.editIcon}
									onClick={() =>
										openEditModal(
											"about",
											"About Me",
											"textarea"
										)
									}
								/>
							</div>
							<textarea
								value={formData.about}
								readOnly={true}
								className={`${styles.bioArea} ${styles.displayInput}`}
								placeholder="Tell us about yourself."
								onClick={() =>
									openEditModal(
										"about",
										"About Me",
										"textarea"
									)
								}
							/>
						</div>
					</form>
				);
		}
	};

	// --- Effects ---

	useEffect(() => {
		if (authCtx.isLoggedIn) {
			getUserProfileData();
		}
	}, [authCtx.isLoggedIn]);

	useEffect(() => {
		if (activeTab === "saved" && authCtx.isLoggedIn) {
			getSavedJobs();
		}
	}, [activeTab, authCtx.isLoggedIn]);

	useEffect(() => {
		if (!hasMore || loadingMore || !lastJobRef.current) return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					loadMoreSavedJobs();
				}
			},
			{ threshold: 1.0 }
		);
		observer.observe(lastJobRef.current);
		return () => {
			observer.disconnect();
		};
	}, [hasMore, loadingMore, savedJobs]);

	useEffect(() => {
		const path = location.pathname;
		if (path === "/profile") setActiveTab("profile");
		else if (path === "/profile/appliedjobs") setActiveTab("applied");
		else if (path === "/profile/savedjobs") setActiveTab("saved");
	}, [location.pathname]);

    // --- NEW: Scroll to top effect when activeTab changes ---
    useEffect(() => {
        // Desktop Container Scroll
        if (contentRef.current) {
            contentRef.current.scrollTo({ top: 0, behavior: "smooth" });
        }
        // Mobile Window Scroll
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [activeTab]);

	return (
		<div className={styles.wrapper}>
			<Navbar />
			<div className={styles.container}>
				{/* Sidebar */}
				<aside className={styles.sidebar}>
					<div className={styles.sidebarHeader}>
						<div className={styles.avatarWrapper}>
							{renderProfileAvatar()}
							<input
								type="file"
								ref={fileInputRef}
								onChange={handleFileChange}
								className={styles.hiddenInput}
								accept="image/*"
							/>
						</div>
						<h2 className={styles.userName}>
						 {user?.username || "Stylist"}
						</h2>
						<p className={styles.userRole}>{userRole}</p>
						{secondaryWorkDomain && (
							<p className={styles.userRole}>
								{secondaryWorkDomain}
							</p>
						)}
						{tertiaryWorkDomain && (
							<p className={styles.userRole}>
								{tertiaryWorkDomain}
							</p>
						)}
					</div>

					<nav className={styles.navMenu}>
						<button
							className={`${styles.navItem} ${
								activeTab === "profile" ? styles.active : ""
							}`}
							onClick={() => navigate("/profile")}
						>
							<FaUser className={styles.icon} /> Profile
						</button>
						<button
							className={`${styles.navItem} ${
								activeTab === "saved" ? styles.active : ""
							}`}
							onClick={() => navigate("/profile/savedjobs")}
						>
							<FaHeart className={styles.icon} /> Saved Jobs
						</button>
					</nav>

					<div className={styles.sidebarFooter}>
						<button
							className={styles.logoutBtn}
							onClick={handleLogout}
						>
							<FaSignOutAlt className={styles.icon} /> Logout
						</button>
					</div>
				</aside>

				{/* Main Content */}
				<main className={styles.mainContent}>
					<div className={styles.mobileProfileHeader}>
						<div className={styles.avatarWrapper}>
							{renderProfileAvatar()}
						</div>
						<div className={styles.contentWrapper}>
							<h2 className={styles.userName}>
								{user?.username || "Stylist"}
							</h2>
							<p className={styles.userRole}>{userRole}</p>
							{secondaryWorkDomain && (
								<p className={styles.userRole}>
									{secondaryWorkDomain}
								</p>
							)}
							{tertiaryWorkDomain && (
								<p className={styles.userRole}>
									{tertiaryWorkDomain}
								</p>
							)}
						</div>
					</div>

					<header className={styles.contentHeader}>
						<div className={styles.headerLeft}>
							<h1 className={styles.pageTitle}>
								{activeTab === "profile"
									? "My Profile"
									: "Saved Jobs"}
							</h1>
						</div>
					</header>

					<div className={styles.mobileTabs}>
						<button
							className={`${styles.mobileTabItem} ${
								activeTab === "profile"
									? styles.mobileActive
									: ""
							}`}
							onClick={() => navigate("/profile")}
						>
							Profile
						</button>
						<button
							className={`${styles.mobileTabItem} ${
								activeTab === "saved" ? styles.mobileActive : ""
							}`}
							onClick={() => navigate("/profile/savedjobs")}
						>
							Saved
						</button>
					</div>

                    {/* Attached contentRef here */}
					<div className={styles.contentBody} ref={contentRef}>
                        {renderContent()}
                    </div>
					<div className={styles.mobileSidebarFooter}>
						<button
							className={styles.mobileLogoutBtn}
							onClick={handleLogout}
						>
							<FaSignOutAlt className={styles.icon} /> Logout
						</button>
					</div>
				</main>
			</div>

			{/* --- EDIT MODAL --- */}
			{editModal.isOpen && (
				<div className={styles.modalOverlay}>
					<div className={styles.modalContent}>
						<div className={styles.modalHeader}>
							<h3>Edit {editModal.label}</h3>
							<button
								className={styles.closeModalBtn}
								onClick={closeEditModal}
							>
								<FaTimes />
							</button>
						</div>
						<div className={styles.modalBody}>
							{editModal.type === "select" ? (
								<select
									ref={editInputRef}
									value={editValue}
									onChange={(e) =>
										setEditValue(e.target.value)
									}
									className={styles.modalInput}
								>
									<option value="">
										Select {editModal.label}
									</option>
									<option value="UNMARRIED">Unmarried</option>
									<option value="MARRIED">Married</option>
									<option value="DIVORCED">Divorced</option>
									<option value="OTHER">Other</option>
								</select>
							) : editModal.type === "textarea" ? (
								<textarea
									ref={editInputRef}
									value={editValue}
									onChange={(e) =>
										setEditValue(e.target.value)
									}
									className={`${styles.modalInput} ${styles.modalTextArea}`}
									placeholder={`Enter your ${editModal.label}`}
								/>
							) : (
								<input
									ref={editInputRef}
									type={editModal.fieldKey === 'whatsapp' ? 'tel' : 'text'}
									value={editValue}
									onChange={(e) => {
										const value = e.target.value;

										// Sirf numbers allow kare
										if (/^\d*$/.test(value)) {
											// Max 10 digits
											if (value.length <= 10) {
												setEditValue(value);
											}
										}
									}}
									className={styles.modalInput}
									placeholder={`Enter your ${editModal.label}`}
									maxLength={10}
									pattern="[0-9]{10}"
									inputMode="numeric"
								/>
							)}
						</div>
						<div className={styles.modalFooter}>
							<button
								className={styles.cancelBtn}
								onClick={closeEditModal}
								disabled={isSavingField}
							>
								Cancel
							</button>
							<button
								className={styles.saveBtn}
								onClick={saveEditModal}
								disabled={isSavingField || editValue === formData[editModal.fieldKey] || (editModal.fieldKey === 'whatsapp' && editValue.length < 10)}
							>
								{isSavingField ? <><FaSpinner className={styles.spinner} /> Saving...</> : "Save Changes"}
							</button>
						</div>
					</div>
				</div>
			)}

		</div>
	);
};

export default Profile;
